import { zodResolver } from '@hookform/resolvers/zod';
import { Paperclip, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  ASSESSMENT_FILE_EXTENSIONS,
  RECORDED_HOW_LABELS,
  RECORDED_HOWS,
  correctResultSchema,
  recordResultSchema,
  type CorrectResultInput,
  type RecordResultInput,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DatePicker } from '../../../components/DatePicker';
import { Field, FieldRow } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { isScoreValid } from '../results-rules';
import styles from './Assessments.module.css';

/**
 * F9.18 — "Record a result" on ⑦ (ADR 0047, DECISIONS-LOG 2026-10-16 Q2): a test sat on paper
 * in the office or taken on another site. Opens beside its button (CLAUDE.md §5); Save shows
 * the result in the row at once and the server confirms after (§5a rule 1).
 */

const SCORESHEET_ACCEPT = ASSESSMENT_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');

/** Today on the recruiter's own calendar, as `YYYY-MM-DD`. */
function today(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

interface RecordResultPanelProps {
  readonly candidateName: string;
  /** The job's test name, offered as the default. */
  readonly testName: string;
  readonly applicationVersion: number;
  readonly onSave: (input: RecordResultInput, scoresheet: File | null) => void;
  readonly onCancel: () => void;
}

export function RecordResultPanel({ candidateName, testName, applicationVersion, onSave, onCancel }: RecordResultPanelProps): ReactNode {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [scoresheet, setScoresheet] = useState<File | null>(null);
  const form = useForm<RecordResultInput>({
    resolver: zodResolver(recordResultSchema),
    defaultValues: { testName, how: 'paper', platform: null, takenOn: today(), version: applicationVersion },
  });
  const how = useWatch({ control: form.control, name: 'how' });
  const [typedScore, typedOutOf] = useWatch({ control: form.control, name: ['score', 'outOf'] });
  const errors = form.formState.errors;

  return (
    <form className={styles.confirm} noValidate onSubmit={form.handleSubmit((input) => onSave(input, scoresheet))}>
      <p className={styles.confirmTitle}>Record {candidateName}'s result</p>
      <Field label="Test" isRequired error={errors.testName?.message}>
        {({ inputId, isInvalid }) => <Input id={inputId} isInvalid={isInvalid} autoFocus placeholder="Line operator test" {...form.register('testName')} />}
      </Field>
      <Controller
        control={form.control}
        name="how"
        render={({ field }) => (
          <Segmented label="How it was taken" value={field.value} options={RECORDED_HOWS.map((value) => ({ value, label: RECORDED_HOW_LABELS[value] }))} onChange={field.onChange} />
        )}
      />
      {how === 'elsewhere' && (
        <Field label="Site" isOptional error={errors.platform?.message}>
          {({ inputId, isInvalid }) => (
            <Input id={inputId} isInvalid={isInvalid} placeholder="HackerRank, Google Forms…" {...form.register('platform', { setValueAs: (value: string) => (value.trim() === '' ? null : value) })} />
          )}
        </Field>
      )}
      <FieldRow>
        <Field label="Score" isRequired error={errors.score?.message}>
          {({ inputId, isInvalid }) => <Input id={inputId} isInvalid={isInvalid} isNumeric type="number" inputMode="decimal" min={0} step="0.5" {...form.register('score', { valueAsNumber: true })} />}
        </Field>
        <Field label="Out of" isRequired error={errors.outOf?.message}>
          {({ inputId, isInvalid }) => <Input id={inputId} isInvalid={isInvalid} isNumeric type="number" inputMode="decimal" min={1} step="0.5" {...form.register('outOf', { valueAsNumber: true })} />}
        </Field>
      </FieldRow>
      <Field label="Taken on" isRequired error={errors.takenOn?.message}>
        {({ inputId, isInvalid }) => (
          <Controller control={form.control} name="takenOn" render={({ field }) => <DatePicker id={inputId} label="Taken on" isInvalid={isInvalid} value={field.value} onChange={field.onChange} />} />
        )}
      </Field>
      <div className={styles.actionsRow}>
        <input
          ref={fileRef}
          type="file"
          hidden
          accept={SCORESHEET_ACCEPT}
          onChange={(event) => {
            setScoresheet(event.target.files?.[0] ?? null);
            event.target.value = '';
          }}
        />
        {scoresheet ? (
          <>
            <span className={styles.meta}>{scoresheet.name}</span>
            <Button variant="ghost" size="sm" icon={X} aria-label="Remove the scoresheet" onClick={() => setScoresheet(null)} />
          </>
        ) : (
          <Button variant="quiet" size="sm" icon={Paperclip} onClick={() => fileRef.current?.click()}>
            Attach the scoresheet
          </Button>
        )}
      </div>
      <p className={styles.confirmBody}>Judged by the job's pass mark. It only suggests the next step; you make the move.</p>
      <div className={styles.confirmActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" type="submit" disabled={!isScoreValid(typedScore, typedOutOf)}>
          Save result
        </Button>
      </div>
    </form>
  );
}

interface CorrectResultPanelProps {
  readonly candidateName: string;
  readonly score: number;
  readonly outOf: number;
  readonly attemptVersion: number;
  readonly onSave: (input: CorrectResultInput) => void;
  readonly onCancel: () => void;
}

/** A typing mistake put right, while the person is still in Assessment. */
export function CorrectResultPanel({ candidateName, score, outOf, attemptVersion, onSave, onCancel }: CorrectResultPanelProps): ReactNode {
  const form = useForm<CorrectResultInput>({
    resolver: zodResolver(correctResultSchema),
    defaultValues: { score, outOf, version: attemptVersion },
  });
  const errors = form.formState.errors;
  const [typedScore, typedOutOf] = useWatch({ control: form.control, name: ['score', 'outOf'] });

  return (
    <form className={styles.confirm} noValidate onSubmit={form.handleSubmit(onSave)}>
      <p className={styles.confirmTitle}>Correct {candidateName}'s score</p>
      <FieldRow>
        <Field label="Score" isRequired error={errors.score?.message}>
          {({ inputId, isInvalid }) => <Input id={inputId} isInvalid={isInvalid} autoFocus isNumeric type="number" inputMode="decimal" min={0} step="0.5" {...form.register('score', { valueAsNumber: true })} />}
        </Field>
        <Field label="Out of" isRequired error={errors.outOf?.message}>
          {({ inputId, isInvalid }) => <Input id={inputId} isInvalid={isInvalid} isNumeric type="number" inputMode="decimal" min={1} step="0.5" {...form.register('outOf', { valueAsNumber: true })} />}
        </Field>
      </FieldRow>
      <div className={styles.confirmActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" type="submit" disabled={!isScoreValid(typedScore, typedOutOf)}>
          Save
        </Button>
      </div>
    </form>
  );
}
